// src/pages/OfficialControlScreen.js  (UPDATED FULL FILE)
// ✅ Uses ONLY /api/vehicles/{PLATE}/qr and reads json.drivers + json.owners
// ✅ Prevents route names like "official" from being used as vehicle plate refs
// ✅ Shows Drivers + Owners cards at the END (Owners is the last card)
// ✅ Fully bilingual via i18n keys

import React, { useEffect, useMemo, useState } from "react";
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Platform,
  ActivityIndicator,
} from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useTranslation } from "../i18n";

const API_BASE_URL = "https://api.taxi-yaounde.com";

const RESERVED_ROUTE_REFS = new Set([
  "OFFICIAL",
  "OFFICIAL-CONTROL",
  "OFFICIAL_CONTROL",
  "OFFICIAL_CONTROL_SCREEN",
  "DRIVER",
  "HOME",
]);

const normalizeRef = (v) =>
  String(v || "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .replace(/\s+/g, "")
    .toUpperCase();

const isValidPlateRef = (v) => {
  const n = normalizeRef(v);

  if (!n) return false;
  if (RESERVED_ROUTE_REFS.has(n)) return false;

  // Prevent paths like "official/ABC123" from becoming a plate.
  if (n.includes("/")) return false;

  return true;
};

const getRefFromPathname = () => {
  if (Platform.OS !== "web") return null;

  try {
    const url = new URL(window.location.href);

    // Support direct links like:
    // /official?ref=ABC123
    // /official?plate=ABC123
    const queryRef =
      url.searchParams.get("ref") ||
      url.searchParams.get("plate") ||
      url.searchParams.get("immatriculation");

    if (isValidPlateRef(queryRef)) {
      return queryRef;
    }

    const segs = (url.pathname || "").split("/").filter(Boolean);

    // Support direct links like:
    // /official/ABC123
    // /official-control/ABC123
    // /official_control_screen/ABC123
    const officialIndex = segs.findIndex((s) => {
      const n = normalizeRef(s);
      return RESERVED_ROUTE_REFS.has(n);
    });

    if (officialIndex !== -1) {
      const candidate = segs[officialIndex + 1];
      return isValidPlateRef(candidate) ? candidate : null;
    }

    // Support direct links like:
    // /ABC123
    if (segs.length === 1 && isValidPlateRef(segs[0])) {
      return segs[0];
    }

    return null;
  } catch {
    return null;
  }
};

// ✅ normalize insurance status string
const normStatus = (s) =>
  String(s || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// ✅ decide chip color by status text
const getStatusTone = (rawStatus) => {
  const s = normStatus(rawStatus);

  const isExpired =
    s.includes("EXPIRE") || s.includes("EXPIR") || s === "EXPIREE" || s === "EXPIRED";

  const isValid = ["VALIDE", "VALID", "ACTIVE", "ACTIF"].includes(s);

  if (isExpired) return "danger";
  if (isValid) return "success";
  return "neutral";
};

// ✅ initials helper
const initialsFromName = (name) => {
  const s = String(name ?? "").trim();
  if (!s) return "—";
  const parts = s.split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((x) => (x[0] ? x[0].toUpperCase() : ""))
    .join("");
};

// ✅ normalize people arrays from API into [{ personId, fullName }]
const normalizePeople = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => {
      const personId = x?.person_id ?? x?.personId ?? x?.id ?? null;
      const fullName = String(x?.name || x?.fullName || x?.noms || "").trim();
      if (!fullName) return null;
      return { personId, fullName };
    })
    .filter(Boolean);
};

export default function OfficialControlScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { t } = useTranslation();

  const refFromParams = route?.params?.ref ?? null;
  const refFromPath = getRefFromPathname();

  const ref = useMemo(() => {
    const raw = refFromParams || refFromPath || null;
    const n = normalizeRef(raw);

    if (!isValidPlateRef(n)) return null;

    return n;
  }, [refFromParams, refFromPath]);

  const EMPTY = useMemo(
    () => ({
      identificationVehicule: {
        immatriculation: ref || "—",
        marqueModele: "—",
        proprietaire: "—",
      },
      autorisationExercer: {
        numeroLicence: "—",
        validiteDebut: "—",
        validiteFin: "—",
        zone: "—",
        numeroPortiere: "—",
      },
      profilChauffeur: {
        noms: "—",
        numeroPermis: "—",
        categorie: "—",
        numeroCNI: "—",
      },
      assurance: {
        compagnie: "—",
        expiration: "—",
        statut: "—",
      },
      drivers: [],
      owners: [],
    }),
    [ref]
  );

  const [payload, setPayload] = useState(EMPTY);

  // ✅ Drivers + Owners from same QR payload
  const [drivers, setDrivers] = useState([]);
  const [owners, setOwners] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);

  useEffect(() => {
    if (!ref) {
      setPayload(EMPTY);
      setDrivers([]);
      setOwners([]);
      setPeopleLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setPeopleLoading(true);

        const r = await fetch(
          `${API_BASE_URL}/api/vehicles/${encodeURIComponent(ref)}/qr`
        );

        const json = await r.json();

        if (cancelled) return;

        if (json?.success) {
          setPayload(json);
          setDrivers(normalizePeople(json?.drivers));
          setOwners(normalizePeople(json?.owners));
        } else {
          setPayload(EMPTY);
          setDrivers([]);
          setOwners([]);
        }
      } catch {
        if (!cancelled) {
          setPayload(EMPTY);
          setDrivers([]);
          setOwners([]);
        }
      } finally {
        if (!cancelled) setPeopleLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ref, EMPTY]);

  const v = payload.identificationVehicule || EMPTY.identificationVehicule;
  const a = payload.autorisationExercer || EMPTY.autorisationExercer;
  const d = payload.profilChauffeur || EMPTY.profilChauffeur;
  const i = payload.assurance || EMPTY.assurance;

  const initials =
    d?.noms && d.noms !== "—"
      ? d.noms
          .split(" ")
          .filter(Boolean)
          .map((x) => x[0])
          .slice(0, 2)
          .join("")
      : "—";

  const statusTone = useMemo(() => getStatusTone(i?.statut), [i?.statut]);

  return (
    <SafeAreaView style={styles.safe}>
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        {/* Back */}
        <View style={styles.topRow}>
          <Pressable style={styles.backBtn} onPress={() => navigation.goBack()}>
            <Text style={styles.backIcon}>‹</Text>
            <Text style={styles.backText}>{t("common.back")}</Text>
          </Pressable>
        </View>

        {/* Vehicle */}
        <SectionCard
          icon="🚕"
          title={t("official.vehicle.title")}
          subtitle={`(${t("official.vehicle.subtitle")})`}
          rows={[
            { icon: "🚗", label: t("official.vehicle.plate"), value: v.immatriculation || "—" },
            { icon: "🚕", label: t("official.vehicle.model"), value: v.marqueModele || "—" },
            { icon: "📍", label: t("official.vehicle.owner"), value: v.proprietaire || "—" },
          ]}
        />

        {/* Licence */}
        <SectionCard
          icon="✅"
          title={t("official.licence.title")}
          subtitle={`(${t("official.licence.subtitle")})`}
          rows={[
            { icon: "🚗", label: t("official.licence.number"), value: a.numeroLicence ?? "—" },
            {
              icon: "🕒",
              label: t("official.licence.validity"),
              value:
                a.validiteDebut && a.validiteFin && a.validiteDebut !== "—" && a.validiteFin !== "—"
                  ? `${a.validiteDebut} au ${a.validiteFin}`
                  : "—",
            },
            { icon: "📍", label: t("official.licence.zone"), value: a.zone ?? "—" },
            { icon: "🚕", label: t("official.licence.doorNumber"), value: a.numeroPortiere ?? "—" },
          ]}
          headerVariant="green"
        />

        {/* Driver */}
        <SectionCard
          icon="🧑🏾‍✈️"
          title={t("official.driver.title")}
          subtitle={`(${t("official.driver.subtitle")})`}
          rows={[
            { icon: "🚗", label: t("official.driver.permit"), value: d.numeroPermis ?? "—" },
            { icon: "🅱️", label: t("official.driver.category"), value: d.categorie ?? "—" },
            { icon: "🪪", label: t("official.driver.cni"), value: d.numeroCNI ?? "—" },
          ]}
          headerVariant="green"
          topLeftAvatar={{ initials, name: d.noms ?? "—" }}
        />

        {/* Insurance */}
        <SectionCard
          icon="✅"
          title={t("official.insurance.title")}
          subtitle={`(${t("official.insurance.subtitle")})`}
          rows={[
            { icon: "🚗", label: t("official.insurance.company"), value: i.compagnie ?? "—" },
            { icon: "🕒", label: t("official.insurance.expiry"), value: i.expiration ?? "—" },
            {
              icon: "✅",
              label: t("official.insurance.status"),
              value: i.statut ?? "—",
              isStatus: true,
              statusTone,
            },
          ]}
          headerVariant="green"
        />

        {/* Drivers */}
        <PeopleListCard
          icon="🧑🏾‍✈️"
          titleKey="official.driversAssignedTitle"
          emptyKey="official.driversEmpty"
          people={drivers}
          loading={peopleLoading}
        />

        {/* Owners */}
        <PeopleListCard
          icon="👤"
          titleKey="official.ownersAssignedTitle"
          emptyKey="official.ownersEmpty"
          people={owners}
          loading={peopleLoading}
        />
      </ScrollView>
    </SafeAreaView>
  );
}

function PeopleListCard({ icon, titleKey, emptyKey, people, loading }) {
  const { t } = useTranslation();

  return (
    <View style={styles.card}>
      <View style={[styles.sectionHeader, styles.sectionHeaderGreen]}>
        <Text style={styles.sectionHeaderIcon}>{icon}</Text>
        <View style={{ flex: 1 }}>
          <Text style={styles.sectionHeaderTitle}>{t(titleKey)}</Text>
        </View>
      </View>

      {loading ? (
        <View style={styles.peopleLoadingRow}>
          <ActivityIndicator />
          <Text style={styles.peopleLoadingText}>{t("common.loading")}</Text>
        </View>
      ) : null}

      {!loading && people?.length ? (
        <View style={styles.peopleList}>
          {people.map((p, idx) => {
            const name = String(p?.fullName || "").trim();
            const ini = initialsFromName(name);

            return (
              <View
                key={`${p.personId ?? name}-${idx}`}
                style={[
                  styles.personRow,
                  idx === people.length - 1 ? styles.personRowLast : null,
                ]}
              >
                <View style={styles.personAvatar}>
                  <Text style={styles.personAvatarText}>{ini}</Text>
                </View>

                <Text style={styles.personName}>{name}</Text>
              </View>
            );
          })}
        </View>
      ) : null}

      {!loading && (!people || people.length === 0) ? (
        <View style={styles.emptyPeople}>
          <Text style={styles.emptyPeopleText}>{t(emptyKey)}</Text>
        </View>
      ) : null}
    </View>
  );
}

function SectionCard({ icon, title, subtitle, rows, headerVariant = "green", topLeftAvatar }) {
  const { t } = useTranslation();

  const isMissingValue = (val) => {
    const s = String(val ?? "").trim();
    return !s || s === "—" || s === "-";
  };

  return (
    <View style={styles.card}>
      <View style={[styles.sectionHeader, headerVariant === "green" && styles.sectionHeaderGreen]}>
        <Text style={styles.sectionHeaderIcon}>{icon}</Text>
        <View style={{ flex: 1 }}>
          <Text style={styles.sectionHeaderTitle}>
            {title} <Text style={styles.sectionHeaderSub}>{subtitle}</Text>
          </Text>
        </View>
      </View>

      {topLeftAvatar ? (
        <View style={styles.avatarRow}>
          <View style={styles.avatarCircle}>
            <Text style={styles.avatarText}>{topLeftAvatar.initials}</Text>
          </View>
          <Text style={styles.avatarName}>{topLeftAvatar.name}</Text>
        </View>
      ) : null}

      {rows.map((r, idx) => {
        const tone = r.isStatus ? r.statusTone || "neutral" : null;
        const missing = !r.isStatus && isMissingValue(r.value);

        return (
          <View
            key={`${r.label}-${idx}`}
            style={[styles.row, idx === rows.length - 1 ? styles.rowLast : null]}
          >
            <Text style={styles.rowIcon}>{r.icon}</Text>
            <Text style={styles.rowLabel} numberOfLines={1}>
              {r.label} :
            </Text>

            {r.isStatus ? (
              <View
                style={[
                  styles.statusChip,
                  tone === "danger" && styles.statusChipDanger,
                  tone === "success" && styles.statusChipSuccess,
                  tone === "neutral" && styles.statusChipNeutral,
                ]}
              >
                <Text style={styles.statusChipText}>{r.value}</Text>
              </View>
            ) : missing ? (
              <View style={[styles.statusChip, styles.statusChipDanger]}>
                <Text style={styles.statusChipText}>{t("driver.official.absent")}</Text>
              </View>
            ) : (
              <Text style={styles.rowValue}>{r.value}</Text>
            )}
          </View>
        );
      })}
    </View>
  );
}

const DARK_GREEN = "#0F3C2F";
const HEADER_GREEN = "#DCEEDD";
const TEXT_DARK = "#0B1F14";
const MUTED_GREEN = "#4B6B57";
const CARD_BG = "#FFFFFF";
const PAGE_BG = "#EEF5F4";

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: PAGE_BG },

  scroll: {
    paddingHorizontal: 18,
    paddingTop: 16,
    paddingBottom: 28,
  },

  topRow: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: 12,
  },
  backBtn: {
    backgroundColor: DARK_GREEN,
    borderRadius: 26,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    shadowColor: "#000",
    shadowOpacity: 0.16,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 8 },
    elevation: 6,
  },
  backIcon: { color: "#fff", fontSize: 20, fontWeight: "900", marginTop: -1 },
  backText: { color: "#fff", fontSize: 18, fontWeight: "900" },

  card: {
    marginTop: 16,
    borderRadius: 22,
    backgroundColor: CARD_BG,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 14 },
    elevation: 3,
  },

  sectionHeader: {
    backgroundColor: "#E6EFE9",
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  sectionHeaderGreen: { backgroundColor: HEADER_GREEN },
  sectionHeaderIcon: { fontSize: 20, marginTop: 1 },
  sectionHeaderTitle: { fontSize: 16, fontWeight: "900", color: "#1F6F3D" },
  sectionHeaderSub: { fontSize: 15, fontWeight: "900", color: "#557B64" },

  peopleLoadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  peopleLoadingText: { fontSize: 14, fontWeight: "800", color: MUTED_GREEN },

  peopleList: { width: "100%" },

  personRow: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(15,23,42,0.08)",
  },
  personRowLast: { borderBottomWidth: 0 },

  personAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: "#ECF1EE",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.10)",
    alignItems: "center",
    justifyContent: "center",
  },
  personAvatarText: { fontSize: 18, fontWeight: "900", color: "#0F172A", letterSpacing: 1 },
  personName: { fontSize: 16, fontWeight: "900", color: "#0F172A" },

  emptyPeople: { paddingHorizontal: 14, paddingVertical: 16 },
  emptyPeopleText: { fontSize: 14, fontWeight: "800", color: MUTED_GREEN },

  avatarRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(15,23,42,0.07)",
  },
  avatarCircle: {
    width: 54,
    height: 54,
    borderRadius: 27,
    backgroundColor: "#ECF1EE",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.10)",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: { fontSize: 18, fontWeight: "900", color: "#0F172A", letterSpacing: 1 },
  avatarName: { flex: 1, fontSize: 16, fontWeight: "900", color: "#0F172A" },

  row: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(15,23,42,0.08)",
  },
  rowLast: { borderBottomWidth: 0 },

  rowIcon: { fontSize: 14, width: 22, textAlign: "center" },

  rowLabel: {
    flex: 1,
    fontSize: 14,
    fontWeight: "800",
    color: MUTED_GREEN,
  },

  rowValue: {
    maxWidth: "52%",
    fontSize: 13,
    fontWeight: "800",
    color: TEXT_DARK,
    textAlign: "right",
  },

  statusChip: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 14,
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 2,
  },
  statusChipSuccess: { backgroundColor: "#3EA84A" },
  statusChipDanger: { backgroundColor: "#DC2626" },
  statusChipNeutral: { backgroundColor: "#6B7280" },

  statusChipText: { color: "#FFFFFF", fontSize: 13, fontWeight: "900", letterSpacing: 0.5 },
});